import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, Mail, Phone } from "lucide-react";
import Seo, { ORG_LD } from "@/components/site/Seo";
import { RevealLines } from "@/components/site/RevealText";

const PRINCIPLES = [
  { n: "01", title: "Requirement first, catalogue second", body: "We start with what you need — not with what a factory happens to make. The right supplier is the one who fits the specification, not the one who called us last." },
  { n: "02", title: "Engineer-led sourcing", body: "Someone who can read drawings and specifications is involved in every requirement. That's how we avoid the classic sourcing mistake — a cheap quote for the wrong product." },
  { n: "03", title: "Qualified before quoted", body: "Every supplier we present is qualified for capability, quality systems, capacity and export experience. A quotation without qualification is not a shortlist." },
  { n: "04", title: "One accountable partner", body: "One point of contact from requirement to delivery. If something moves, we move it — and we tell you what we're doing, honestly." },
  { n: "05", title: "Truthful commercial discipline", body: "We are not a marketplace and not a broker. We are a sourcing partner. If we can't help with something, we say so — and often point you in the right direction anyway." },
];


export default function About() {
  return (
    <div className="bg-nx-bg text-nx-ink" data-testid="page-about">
      <Seo
        title="About NEXORIQ · A Global Sourcing Partner Built for Difficult Requirements"
        description="NEXORIQ FZ LLC is a UAE-based global sourcing and supply chain partner. Engineer-led, technically capable, discreet — helping industrial companies source what standard channels cannot."
        path="/about"
        structuredData={ORG_LD}
      />
      <section className="bg-nx-navydark text-nx-bg px-6 md:px-12 lg:px-20 pt-32 md:pt-40 pb-24">
        <div className="font-mono text-[0.65rem] uppercase tracking-[0.28em] text-nx-copper mb-6">About NEXORIQ</div>
        <RevealLines
          as="h1"
          lines={[<>A global sourcing partner</>, <>built for difficult requirements.</>]}
          className="font-cabinet font-black tracking-[-0.04em] leading-[0.92] text-[13vw] md:text-[7vw] lg:text-[5.4vw] text-nx-bg"
        />
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-6 text-nx-bg/85 leading-relaxed text-lg">
            <p>
              NEXORIQ FZ LLC is a UAE-based global sourcing and supply chain company. We were
              created to help companies overcome the complexity of global sourcing — the moment a
              requirement stops being routine.
            </p>
            <p>
              We connect industrial buyers with capable suppliers across global manufacturing markets.
              We combine sourcing expertise, technical understanding and commercial discipline to help
              customers find the right solution, not just the first quote.
            </p>
            <p>
              We are not a marketplace, not a broker, and not a logistics-only company. We are the
              sourcing partner companies call when normal sourcing channels are not enough.
            </p>
          </div>
          <aside className="lg:col-span-5 border-l border-nx-bg/20 lg:pl-10 space-y-6 text-nx-bg/85">
            <div>
              <div className="font-mono text-[0.6rem] uppercase tracking-[0.24em] text-nx-copper mb-2">Registered</div>
              <div className="flex items-start gap-3 text-sm">
                <MapPin size={14} className="text-nx-copper flex-shrink-0 mt-1" />
                NEXORIQ FZ LLC · VUNE2559 Compass Building · Al Hulaila Industrial Zone-FZ · P.O. Box 10055 · Ras Al Khaimah · United Arab Emirates
              </div>
            </div>
            <div>
              <div className="font-mono text-[0.6rem] uppercase tracking-[0.24em] text-nx-copper mb-2">Contact</div>
              <div className="space-y-2 text-sm">
                <a href="mailto:source@nexoriq.co" className="flex items-center gap-3 nx-underline text-nx-bg" data-testid="about-email">
                  <Mail size={13} className="text-nx-copper" /> source@nexoriq.co
                </a>
                <a href="tel:+971508003202" className="flex items-center gap-3 nx-underline text-nx-bg" data-testid="about-phone">
                  <Phone size={13} className="text-nx-copper" /> +971 50 800 3202
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-24">
        <div className="font-mono text-[0.65rem] uppercase tracking-[0.28em] text-nx-copper mb-6">Our approach</div>
        <h2 className="font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[5vw] lg:text-[4.4vw] text-nx-navy max-w-4xl">
          Five principles that shape how we work.
        </h2>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-14">
          {PRINCIPLES.map((p, i) => (
            <motion.article
              key={p.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="border-t border-nx-navy pt-5"
              data-testid={`principle-${p.n}`}
            >
              <div className="font-mono text-[0.62rem] uppercase tracking-[0.24em] text-nx-copper mb-3">{p.n}</div>
              <h3 className="font-cabinet font-black text-2xl tracking-tight text-nx-navy leading-tight">{p.title}</h3>
              <p className="mt-4 text-nx-navy2 leading-relaxed">{p.body}</p>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="bg-nx-navydark text-nx-bg px-6 md:px-12 lg:px-20 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <h2 className="font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[5.4vw] text-nx-bg">
              Let's help you source it.
            </h2>
            <p className="mt-6 text-nx-bg/75 max-w-xl leading-relaxed">
              Have a requirement in mind? Send it across. Standard response window is 48 hours; urgent
              scope is same-day.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/sourcing-request" className="nx-btn on-dark" data-testid="about-cta">
              Submit a Requirement <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
